import { useEffect, useState } from "react";

const KEY = "spur_comfort";
const EVT = "spur-comfort-change";

const stored = () => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};

/** Device is asking for larger text / more contrast (browser font-size bumped above 16px,
 *  prefers-contrast: more, or forced-colors). Mirrors the pre-paint check in public/index.html. */
export const deviceWantsComfort = () => {
  try {
    const mq = (q) => window.matchMedia && window.matchMedia(q).matches;
    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const basePx = document.documentElement.classList.contains("comfort") ? rootPx / 1.125 : rootPx;
    return basePx > 16.5 || mq("(prefers-contrast: more)") || mq("(forced-colors: active)");
  } catch {
    return false;
  }
};

/** Explicit choice wins; otherwise follow the device. */
export const isComfort = () => {
  const v = stored();
  if (v === "1") return true;
  if (v === "0") return false;
  return deviceWantsComfort();
};

export const isAutoComfort = () => stored() === null && deviceWantsComfort();

const paint = (on) => {
  document.documentElement.classList.toggle("comfort", on);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", on ? "#17120f" : "#050505");
};

export function setComfort(on) {
  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    /* private mode: still apply for this page view */
  }
  paint(on);
  window.dispatchEvent(new Event(EVT));
}

/** Per-device "Comfort Mode" preference (warmer bg, softer lime, white text, bigger type). */
export function useComfort() {
  const [comfort, setState] = useState(isComfort);
  useEffect(() => {
    paint(isComfort());
    const sync = () => {
      paint(isComfort());
      setState(isComfort());
    };
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    const mqs = ["(prefers-contrast: more)", "(forced-colors: active)"]
      .map((q) => (window.matchMedia ? window.matchMedia(q) : null))
      .filter(Boolean);
    mqs.forEach((m) => m.addEventListener?.("change", sync));
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
      mqs.forEach((m) => m.removeEventListener?.("change", sync));
    };
  }, []);
  return { comfort, toggle: () => setComfort(!isComfort()) };
}
